import React, { useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import Layout from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ChevronLeft, User, Calendar, MessageCircle, Edit2, Trash2, Send, Edit, Trash } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

interface User {
  id: number;
  name: string;
  email: string;
}

interface Discussion {
  id: number;
  title: string;
  content: string;
  user: User;
  offer: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

interface Comment {
  id: number;
  content: string;
  user: User;
  created_at: string;
  updated_at: string;
}

interface ShowProps {
  discussion: Discussion;
  comments: Comment[];
  is_admin: boolean;
}

function Show({ discussion, comments, is_admin }: ShowProps) {
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editContent, setEditContent] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditDiscussionDialogOpen, setIsEditDiscussionDialogOpen] = useState(false);
  const [discussionTitle, setDiscussionTitle] = useState("");
  const [discussionContent, setDiscussionContent] = useState("");

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setIsSubmitting(true);
    router.post(
      `/discussions/${discussion.id}/discussion_comments`,
      {
        discussion_comment: {
          content: newComment,
        },
      },
      {
        preserveState: false,
        preserveScroll: false,
        onSuccess: () => {
          setNewComment("");
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setEditContent(comment.content);
    setIsEditDialogOpen(true);
  };

  const handleUpdateComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingComment) return;
    
    setIsSubmitting(true);
    router.patch(
      `/discussions/${discussion.id}/discussion_comments/${editingComment.id}`,
      {
        discussion_comment: {
          content: editContent,
        },
      },
      {
        preserveState: false,
        preserveScroll: false,
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingComment(null);
          setEditContent("");
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const handleDeleteComment = (commentId: number) => {
    if (confirm("Are you sure you want to delete this comment?")) {
      router.delete(`/discussions/${discussion.id}/discussion_comments/${commentId}`, {
        preserveState: false,
        preserveScroll: false,
      });
    }
  };

  const handleEditDiscussion = () => {
    setDiscussionTitle(discussion.title);
    setDiscussionContent(discussion.content);
    setIsEditDiscussionDialogOpen(true);
  };

  const handleUpdateDiscussion = (e: React.FormEvent) => {
    e.preventDefault();
    
    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');
    
    setIsSubmitting(true);
    router.patch(
      `/offers/${discussion.offer.id}/offer_discussions/${discussion.id}?from=${from || ''}`,
      {
        discussion: {
          title: discussionTitle,
          content: discussionContent,
        },
      },
      {
        preserveState: false,
        preserveScroll: false,
        onSuccess: () => {
          setIsEditDiscussionDialogOpen(false);
          setDiscussionTitle("");
          setDiscussionContent("");
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  const handleDeleteDiscussion = () => {
    if (confirm("Are you sure you want to delete this discussion? This action cannot be undone.")) {
      const urlParams = new URLSearchParams(window.location.search);
      const from = urlParams.get('from');
      
      router.delete(`/offers/${discussion.offer.id}/offer_discussions/${discussion.id}?from=${from || ''}`, {
        preserveState: false,
        preserveScroll: false,
      });
    }
  };

  // Get current user from meta tag or props (you might need to pass this from controller)
  const currentUserEmail = document.querySelector('meta[name="current-user-email"]')?.getAttribute('content');
  
  // Get the 'from' parameter to determine which back link to show
  const urlParams = new URLSearchParams(window.location.search);
  const from = urlParams.get('from');

  return (
    <Layout>
      <Head title={`${discussion.title} | Mailkit - Resolute`} />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            {from === 'offer' ? (
              <Link
                href={`/offers/${discussion.offer.id}?tab=discussions`}
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-4"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to {discussion.offer.name} Discussions
              </Link>
            ) : (
              <Link
                href="/discussions"
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mb-4"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to All Discussions
              </Link>
            )}
          </div>

          {/* Discussion Topic */}
          <Card className="bg-white rounded-xl border border-gray-200 mb-6 shadow-none">
            <CardHeader className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-xl font-semibold text-gray-900 mb-2">{discussion.title}</h1>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{discussion.user.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(discussion.created_at)}</span>
                    </div>
                  </div>
                </div>
                {is_admin && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditDiscussion}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 cursor-pointer"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteDiscussion}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-6 py-4">
              <p className="text-gray-700 whitespace-pre-wrap">{discussion.content}</p>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <div className="space-y-4 mt-8">
            <h2 className="text-lg font-semibold text-gray-900">
              Comments ({comments.length})
            </h2>

            {/* Comments List */}
            {comments.length > 0 ? (
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{comment.user.name}</span>
                          </div>
                          <span className="text-sm text-gray-400">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {comment.content}
                        </p>
                      </div>
                      {(comment.user.email === currentUserEmail || is_admin) && (
                        <div className="flex items-center gap-1 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditComment(comment)}
                            className="text-gray-400 hover:text-gray-600 hover:bg-gray-50 cursor-pointer"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg border border-gray-200 py-12 text-center">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  No comments yet. Be the first to comment!
                </p>
              </div>
            )}
          </div>

          {/* Add Comment Form */}
          <div className="space-y-4 mt-8">
            <h3 className="text-lg font-semibold text-gray-900">Add a Comment</h3>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <form onSubmit={handleSubmitComment}>
                  <div className="space-y-4">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write your comment here..."
                      rows={4}
                      required
                      className="w-full border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg resize-none"
                    />
                    <div className="flex justify-end">
                      <Button 
                        type="submit" 
                        disabled={isSubmitting || !newComment.trim()}
                        className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {isSubmitting ? "Posting..." : "Post Comment"}
                      </Button>
                    </div>
                  </div>
                </form>
            </div>
          </div>

          {/* Edit Comment Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Comment</DialogTitle>
                <DialogDescription>
                  Make changes to your comment below.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateComment}>
                <div className="my-4">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={4}
                    required
                    className="w-full"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Updating..." : "Update Comment"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Discussion Dialog */}
          <Dialog open={isEditDiscussionDialogOpen} onOpenChange={setIsEditDiscussionDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit Discussion Topic</DialogTitle>
                <DialogDescription>
                  Update the discussion topic details.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateDiscussion}>
                <div className="space-y-4 my-4">
                  <div>
                    <Label htmlFor="edit-title">Title</Label>
                    <Input
                      id="edit-title"
                      value={discussionTitle}
                      onChange={(e) => setDiscussionTitle(e.target.value)}
                      placeholder="Enter discussion title"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-content">Content</Label>
                    <Textarea
                      id="edit-content"
                      value={discussionContent}
                      onChange={(e) => setDiscussionContent(e.target.value)}
                      placeholder="Enter discussion content"
                      required
                      rows={6}
                      className="mt-1"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditDiscussionDialogOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  >
                    {isSubmitting ? "Updating..." : "Update Topic"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Layout>
  );
}

export default Show;